import React, { Component } from 'react';
import { connect } from 'react-redux';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { Grid } from 'react-bootstrap';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ErrorPopover } from './ErrorPopover';
import { NameTooltip } from './NameTooltip';
import { Repositories } from '../../constants/repositories';
import { Languages } from '../../constants/languages';

import { showViewSourceModal, hideViewSourceModal, setPreviewMediaSource, setMediaRowObject } from '../../actions';
import { ViewSourceModal } from '../viewSourceModal';
import { getBackendUrl } from '../../env';
import { downloadUrl } from '../../helpers';

const errorFormatter = cell => {
    if (cell) {
        return (<ErrorPopover text={cell} />);
    }
};

const nameFormatter = cell => {
    if (cell && cell.length > 25) {
        return (<NameTooltip text={cell} />);
    }

    return cell;
};

const repositoryFormatter = cell => {
    return Repositories[cell];
};

const languageFormatter = cell => {
    return Languages[cell];
};

const errorClass = row => {
    return row.error ? 'tr-error' : '';
};

export class Table extends Component {

    constructor(props) {
        super(props);
        this.imageFormatter = this.imageFormatter.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        const { scene: newScene, mediaItems: newMedia } = nextProps;
        const { scene: oldScene, mediaItems: oldMedia } = this.props;

        if (newScene !== oldScene) {
            // Clear all the filters only when the scene changes
            this.error.cleanFiltered();
            this.mediaName.cleanFiltered();
            this.datasetNum.cleanFiltered();
            this.language.cleanFiltered();
            this.sourceRepository.cleanFiltered();
            this.sourcePath.cleanFiltered();
        }

        // if any are selected in old media AND none are selected in new media, then we refreshed the media
        // reset the selection state in the table.
        if (_.some(oldMedia, m => m.selected) && !_.some(newMedia, m => m.selected)) {
            this.table.cleanSelected();
        }
    }

    imageFormatter(cell, row) {
        return (
            <a href="#" onClick={this.handleSourceClick.bind(this, row)} alt="Preview media" title="Preview media">
                <i className="fas fa-eye"></i>
            </a>
        )
    };

    handleSourceClick(row) {
        const { showViewSourceModal, setPreviewMediaSource, setMediaRowObject } = this.props;
        const srcUrl = downloadUrl(row.sourceRepository, row.sourcePath);
        console.log("Source URL: ", srcUrl);
        setMediaRowObject(row);
        setPreviewMediaSource(srcUrl);
        showViewSourceModal();
    };

    render() {
        const { mediaItems, onBeforeSaveCell, onAfterSaveCell, handleRowSelect, handleAllSelect, languageFilter, mediaPreview, mediaRowObject } = this.props;

        const tableOptions = {
            sizePerPage: 100,
            paginationPosition: 'both',
            hideSizePerPage: false,
            sizePerPageList: [50, 100, 150, 200]
        };

        const cellEditOptions = {
            mode: 'dbclick',
            afterSaveCell: onAfterSaveCell,
            beforeSaveCell: onBeforeSaveCell,
            blurToSave: true
        };

        const rowSelectionOptions = {
            mode: 'checkbox',
            hideSelectColumn: false,
            clickToSelectAndEditCell: true,
            bgColor: 'cornsilk',
            onSelect: handleRowSelect,
            onSelectAll: handleAllSelect
        };

        const datasetFilterOpts = {
            type: 'NumberFilter',
            delay: 200
        };

        // Languages in dropdown should be only whats available
        let languages = [""];
        if (mediaItems) {

            languages = _.pick(Languages, _.map(mediaItems, item => item.language));
        }
        const languageFilterOpts = {
            type: 'CustomFilter',
            getElement: languageFilter,
            customFilterParameters: { items: languages }
        };

        // Repositories in dropdown should be only whats available
        let repos = [""];
        if (mediaItems) {
            repos = _.pick(Repositories, _.map(mediaItems, item => item.sourceRepository));
        }
        const repositoryFilterOpts = {
            type: 'CustomFilter',
            getElement: languageFilter,
            customFilterParameters: { items: repos }
        };

        const mediaNameFilterOpts = {
            type: 'RegexFilter',
            delay: 200
        };

        const languageEditableOpts = {
            type: 'select',
            options: {
                values: _(Languages).transform((result, value, key) => result.push({ text: value, value: key }), []).value()
            }
        };

        const getSrcTitle = url => {
            const urlParts = url.split('/');
            return urlParts[urlParts.length-1];
        };

        const srcTitle = getSrcTitle(mediaPreview);

        return [
            <Grid fluid key='table-grid'>
                <BootstrapTable 
                    ref={x => this.table = x} 
                    data={mediaItems} 
                    cellEdit={cellEditOptions} 
                    options={tableOptions} 
                    selectRow={rowSelectionOptions} 
                    trClassName={errorClass} 
                    multiColumnSort={2} 
                    hover 
                    pagination 
                    condensed
                    striped
                >
                    <TableHeaderColumn dataField='sceneMediaID' isKey hidden={true} />
                    <TableHeaderColumn width='3%' dataFormat={this.imageFormatter} dataAlign='center' />
                    <TableHeaderColumn width='2%' ref={x => this.error = x} dataField='error' dataSort editable={false} dataFormat={errorFormatter} dataAlign='center' />
                    <TableHeaderColumn width='18%' ref={x => this.mediaName = x} dataField='mediaName' dataSort filter={mediaNameFilterOpts} dataFormat={nameFormatter}>MediaName </TableHeaderColumn>
                    <TableHeaderColumn width='10%' ref={x => this.datasetNum = x} dataField='datasetNum' dataSort filter={datasetFilterOpts}>Dataset</TableHeaderColumn>
                    <TableHeaderColumn width='10%' ref={x => this.language = x} dataField='language' dataSort filter={languageFilterOpts} editable={languageEditableOpts} dataFormat={languageFormatter}>Language</TableHeaderColumn>
                    <TableHeaderColumn width='6%' ref={x => this.sourceRepository = x} dataField='sourceRepository' dataSort filter={repositoryFilterOpts} editable={false} dataFormat={repositoryFormatter}>Repository</TableHeaderColumn>
                    <TableHeaderColumn width='51%' ref={x => this.sourcePath = x} dataField='sourcePath' dataSort filter={mediaNameFilterOpts}>SourcePath</TableHeaderColumn>
                </BootstrapTable>
            </Grid>,
            <ViewSourceModal key='viewSrcModal' mediaURL={mediaPreview} title={srcTitle} mediaRowObject={mediaRowObject}/>
        ];
    }
}

Table.propTypes = {
    scene: PropTypes.string,
    mediaItems: PropTypes.array,
    onBeforeSaveCell: PropTypes.func.isRequired,
    onAfterSaveCell: PropTypes.func.isRequired,
    handleRowSelect: PropTypes.func.isRequired,
    handleAllSelect: PropTypes.func.isRequired,
    languageFilter: PropTypes.func.isRequired,
    showViewSourceModal: PropTypes.func.isRequired,
    mediaPreview: PropTypes.string,
    setPreviewMediaSource: PropTypes.func.isRequired,
    setMediaRowObject: PropTypes.func.isRequired,
    mediaRowObject: PropTypes.object.isRequired
};

const mapStateToProps = state => {
    const scene = state.scene.current;
    const mediaPreview = state.ui.previewMedia.mediaPreview;
    const mediaRowObject = state.ui.previewMedia.mediaRowObject;

    return {
        scene,
        mediaPreview,
        mediaRowObject
    };
};

const mapDispatchToProps = ({
    showViewSourceModal,
    hideViewSourceModal,
    setPreviewMediaSource,
    setMediaRowObject
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Table);



// WEBPACK FOOTER //
// ./src/components/table/Table.js